import { Empty, Flex, Skeleton, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import InfiniteScroll from "react-infinite-scroll-component"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate, useParams } from "react-router"

import { RoutesPaths } from "../../../../../../constants/routes.ts"
import type { RootState } from "../../../../../../store.ts"
import { serializeData } from "../../../../../../utils/utils"
import { useAlbumsView } from "../../../../../Album/helpers/albums-view-preference.ts"
import {
  albumSortToOrdering,
  sortAlbumsClientSide
} from "../../../../../Album/helpers/sort-albums.ts"
import { albumSetProp } from "../../../../../Album/redux/album.slice.ts"
import {
  deleteAlbum,
  fetchAlbums,
  fetchMoreAlbums,
  updateAlbum
} from "../../../../../Album/redux/album.thunks.ts"
import { type AlbumsFilters } from "../../../../../Album/redux/album.types.ts"
import {
  DEFAULT_ALBUM_SORT_ORDER,
  type AlbumSortOrder
} from "../../../../../Album/types/album-sort.types.ts"
import type { Album, AlbumDeleteFilesMode } from "../../../../../Album/types/album.types.ts"
import { selectProjectWritePermissions } from "../../../../helpers/project-permissions.selectors.ts"
import { ALBUMS_SCROLL_CONTAINER_ID } from "./albums-scroll-container.ts"
import { DeleteAlbumModal } from "./DeleteAlbum/DeleteAlbumModal.tsx"
import { EditAlbumModal } from "./EditAlbum/EditAlbumModal.tsx"
import {
  AlbumsGridLoader,
  AlbumsGridView
} from "./ProjectAlbums/AlbumsGridView.tsx"
import {
  AlbumsListLoader,
  AlbumsListView
} from "./ProjectAlbums/AlbumsListView.tsx"
import { ProjectAlbumsFilters } from "./ProjectAlbums/ProjectAlbumsFilters.tsx"

const { Title, Paragraph } = Typography

const SCROLLER_CLASSES = "overflow-y-auto tailwind-scroll overflow-x-hidden flex-1"

interface ProjectViewAlbumsSectionOwnProps {
  onCreateAlbumClick?: () => void
}

function ProjectViewAlbumsSection({
  data,
  fetching,
  projectPermissions,
  getAlbums,
  getMoreAlbums,
  filters,
  setProp,
  userId,
  deleteAlbum,
  updateAlbum,
  onCreateAlbumClick
}: ConnectedProps<typeof connector> & ProjectViewAlbumsSectionOwnProps) {
  const { t } = useTranslation()
  const isInitRef = useRef(true)

  const [deletingAlbumId, setDeletingAlbumId] = useState<string>()
  const [isDeletingAlbum, setIsDeletingAlbum] = useState(false)
  const [sortOrder, setSortOrder] = useState<AlbumSortOrder>(
    DEFAULT_ALBUM_SORT_ORDER
  )
  const [view, setView] = useAlbumsView()
  const scrollerRef = useRef<HTMLDivElement>(null)

  const isRefetching = !isInitRef?.current

  const navigate = useNavigate()
  const projectId = useParams()?.id as string

  const albums = data?.results || []
  // Only file-count is still ordered here; everything else is asked of the
  // server, which can order the whole list rather than the page that arrived.
  const sortedAlbums = useMemo(
    () => sortAlbumsClientSide(albums, sortOrder),
    [albums, sortOrder]
  )
  const moduleDocs = projectPermissions?.docs
  const createAllowed = moduleDocs?.project_album_create || false
  const hasSearch = !!filters?.q

  const deleteAllowed = !!moduleDocs?.project_doc_delete
  const deleteForeignAllowed =
    deleteAllowed && !!moduleDocs?.project_doc_manage_others
  const editAllowed = !!moduleDocs?.project_doc_edit
  const [editingAlbumId, setEditingAlbumId] = useState<string>()

  const albumPendingDelete = useMemo(
    () => albums.find((a) => a.id === deletingAlbumId),
    [albums, deletingAlbumId]
  )

  const editingAlbum = useMemo(
    () => albums.find((a) => a.id === editingAlbumId),
    [albums, editingAlbumId]
  )

  const handleEditSubmit = useCallback(
    async (data: Partial<Album>) => {
      if (!editingAlbumId) return
      await updateAlbum({ id: editingAlbumId, data }).unwrap()
      setEditingAlbumId(undefined)
    },
    [updateAlbum, editingAlbumId]
  )

  const handleDelete = useCallback(
    async (files: AlbumDeleteFilesMode) => {
      if (!deletingAlbumId) return
      setIsDeletingAlbum(true)
      try {
        // `unwrap` is the point: a rejected delete otherwise settles as a
        // fulfilled promise and the modal closes over an album still there.
        await deleteAlbum({ id: deletingAlbumId, files }).unwrap()
      } catch {
        return
      } finally {
        setIsDeletingAlbum(false)
      }
      setDeletingAlbumId(undefined)
    },
    [deletingAlbumId, deleteAlbum]
  )

  const updateFilters = useCallback(
    (data: Partial<AlbumsFilters>) => {
      setProp({
        key: "filters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const handleChangeFilter = useCallback(
    (newData: Partial<AlbumsFilters>) => {
      updateFilters({ ...newData, page: 1 })
    },
    [updateFilters]
  )

  // Changing the sort re-asks the server. Page resets with it: page 3 of the
  // old order says nothing about the new one.
  const handleSortChange = useCallback(
    (order: AlbumSortOrder) => {
      setSortOrder(order)
      handleChangeFilter({ ordering: albumSortToOrdering(order) })
    },
    [handleChangeFilter]
  )

  const handleGetAlbums = useCallback(async () => {
    const serializeFilters = serializeData(filters || {})

    await getAlbums({ project: projectId, ...serializeFilters })
    if (isInitRef.current) isInitRef.current = false
  }, [projectId, filters, getAlbums])

  const handleViewAlbum = useCallback(
    (id: string) => {
      navigate(RoutesPaths.project.view(projectId).album(id))
    },
    [projectId, navigate]
  )

  const handleLoadMoreData = useCallback(() => {
    if (!data?.next) return
    const urlObj = new URL(data?.next)
    const params = new URLSearchParams(urlObj.search)
    const paramsObj = Object.fromEntries(params)
    const serializeFilters = serializeData(paramsObj) as AlbumsFilters

    getMoreAlbums({ project: projectId, ...serializeFilters })
  }, [getMoreAlbums, projectId, data?.next])

  useEffect(() => {
    void handleGetAlbums()
  }, [filters, handleGetAlbums])

  // InfiniteScroll asks for the next page from a scroll event alone, so a page
  // that does not overflow the container strands the rest of the list.
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || fetching || !data?.next || !albums.length) return
    if (scroller.scrollHeight <= scroller.clientHeight) handleLoadMoreData()
  }, [albums.length, fetching, data?.next, handleLoadMoreData])

  const albumViewProps = {
    albums: sortedAlbums,
    createAllowed,
    onCreateAlbumClick,
    onView: handleViewAlbum,
    userId,
    deleteAllowed,
    deleteForeignAllowed,
    onDeleteClick: setDeletingAlbumId,
    editAllowed,
    onEditClick: setEditingAlbumId
  }

  const AlbumsLoader = view === "list" ? AlbumsListLoader : AlbumsGridLoader

  const renderAlbums = () =>
    view === "list" ? (
      <AlbumsListView {...albumViewProps} />
    ) : (
      <AlbumsGridView {...albumViewProps} />
    )

  if (!isRefetching && fetching) {
    return (
      <Flex
        vertical
        gap={"small"}
        flex={1}
        className={"overflow-hidden w-full"}
      >
        <Skeleton.Node className={"w-full h-[86px]"} />
        <AlbumsLoader count={20} className={SCROLLER_CLASSES} />
      </Flex>
    )
  }

  return (
    <Flex vertical gap={"middle"} flex={1} className={"overflow-hidden w-full"}>
      <Flex vertical gap={0}>
        <Title level={5} className="!mb-0">
          {t("files.albums.title")}
        </Title>
        <Paragraph type="secondary" className="!mb-0">
          {t("files.albums.subtitle")}
        </Paragraph>
      </Flex>

      <ProjectAlbumsFilters
        showVisibilityFilter={
          !!projectPermissions?.docs?.project_doc_show_visibility_filter
        }
        filters={filters}
        onFiltersChange={handleChangeFilter}
        sortValue={sortOrder}
        onSortChange={handleSortChange}
        view={view}
        onViewChange={setView}
      />

      {fetching && isRefetching ? (
        <AlbumsLoader count={20} className={SCROLLER_CLASSES} />
      ) : hasSearch && !albums.length ? (
        <Flex align="center" justify="center" className="h-[290px]">
          <Empty description={t("files.albums.noSearchResults")} />
        </Flex>
      ) : (
        <div
          ref={scrollerRef}
          id={ALBUMS_SCROLL_CONTAINER_ID}
          className={SCROLLER_CLASSES}
        >
          <InfiniteScroll
            scrollableTarget={ALBUMS_SCROLL_CONTAINER_ID}
            dataLength={sortedAlbums.length}
            // A single element child reads as "no children" to the library,
            // which then pins the loader on screen while a next page exists.
            hasChildren={sortedAlbums.length > 0}
            next={handleLoadMoreData}
            hasMore={!!data?.next}
            loader={<AlbumsLoader />}
          >
            {renderAlbums()}
          </InfiniteScroll>
        </div>
      )}

      {editingAlbum && (
        <EditAlbumModal
          album={editingAlbum}
          onClose={() => setEditingAlbumId(undefined)}
          onSubmit={handleEditSubmit}
        />
      )}

      {albumPendingDelete && (
        <DeleteAlbumModal
          albumName={albumPendingDelete.name}
          fileCount={albumPendingDelete.attachments_count ?? 0}
          isDeleting={isDeletingAlbum}
          onClose={() => setDeletingAlbumId(undefined)}
          onConfirm={(files) => void handleDelete(files)}
        />
      )}
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  fetching: state.album.fetching,
  data: state.album.albums,
  filters: state.album.filters,
  projectPermissions: selectProjectWritePermissions(state),
  userId: state.auth.userInfo.id
})

const mapDispatchToProps = {
  getAlbums: fetchAlbums,
  getMoreAlbums: fetchMoreAlbums,
  setProp: albumSetProp,
  deleteAlbum,
  updateAlbum
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewAlbumsSection)
